import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSend, FiRefreshCw, FiChevronDown, FiChevronRight } from 'react-icons/fi';
const authH = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

export default function AdminBlast({ token }) {
  const campaignId = localStorage.getItem('selectedCampaign') || 'aca';
  const [tab, setTab]             = useState('send');
  // Subaccounts (inactive only — same cache as Subaccounts tab)
  const [subaccounts, setSubaccounts] = useState([]);
  const [subLoading, setSubLoading]   = useState(true);
  const [expanded, setExpanded]       = useState({});
  const [search, setSearch]           = useState('');
  // Per-subaccount selection: { locationId → pct (1-100) }
  const [selSubs, setSelSubs]     = useState({});
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selTmpls, setSelTmpls]   = useState(new Set());
  // Channels
  const [sendSms, setSendSms]     = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [incBlasted, setIncBlasted] = useState(false);
  // Send state
  const [sending, setSending]     = useState(false);
  const [results, setResults]     = useState(null);
  // History
  const [history, setHistory]     = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const loadSubaccounts = useCallback(async () => {
    setSubLoading(true);
    try {
      const r = await axios.get('/api/admin/ghl/subaccounts', authH(token));
      // Only inactive subaccounts (master status OFF)
      const inactive = (r.data.subaccounts||[]).filter(s => s.isActive === false);
      setSubaccounts(inactive);
    } catch(e) { toast.error('Failed to load subaccounts'); } finally { setSubLoading(false); }
  }, [token]);

  const loadTemplates = useCallback(async () => {
    try { const r = await axios.get('/api/admin/message-templates', authH(token)); setTemplates(r.data.templates||[]); }
    catch(e) { toast.error('Failed to load templates'); }
  }, [token]);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try { const r = await axios.get(`/api/admin/blast/history?campaignId=${campaignId}`, authH(token)); setHistory(r.data); }
    catch(e) { toast.error('Failed to load history'); } finally { setHistLoading(false); }
  }, [token, campaignId]);

  useEffect(() => { loadSubaccounts(); loadTemplates(); }, [loadSubaccounts, loadTemplates]);
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  const q = search.toLowerCase();
  const filteredSubs = subaccounts.filter(s =>
    !search.trim() ||
    (s.locationName||'').toLowerCase().includes(q) ||
    (s.locationId||'').toLowerCase().includes(q) ||
    (s.assignedAgentEmail||'').toLowerCase().includes(q)
  );

  const toggleSub = (locationId, pct) => {
    setSelSubs(p => {
      const n = {...p};
      if (n[locationId] !== undefined) { delete n[locationId]; } // deselect
      else { n[locationId] = pct || 10; }
      return n;
    });
  };
  const setPct = (locationId, pct) => setSelSubs(p => ({...p, [locationId]: pct}));
  const toggleT = (id) => setSelTmpls(p => { const n=new Set(p); n.has(id)?n.delete(id):n.add(id); return n; });

  // Compute total leads that will be selected across all selected subaccounts
  const totalLeadsToBlast = Object.entries(selSubs).reduce((sum, [lid, pct]) => {
    const sub = subaccounts.find(s => s.locationId === lid);
    const count = sub?.contactCount || 0;
    return sum + Math.max(1, Math.floor(count * pct / 100));
  }, 0);

  const disabled = sending || Object.keys(selSubs).length === 0 || !selTmpls.size;

  const handleSend = async () => {
    if (!Object.keys(selSubs).length) return toast.warn('Select at least one subaccount');
    if (!selTmpls.size)               return toast.warn('Select at least one template');
    if (!sendSms && !sendEmail)       return toast.warn('Enable SMS or Email');

    // Build request: per-subaccount selections with pct
    const subSelections = Object.entries(selSubs).map(([locationId, pct]) => ({ locationId, pct }));
    if (!window.confirm(`Send blast to ~${totalLeadsToBlast} leads across ${subSelections.length} subaccount(s) using ${selTmpls.size} template(s) round-robin?`)) return;

    setSending(true); setResults(null);
    try {
      const r = await axios.post('/api/admin/blast/send-by-subaccount', {
        subSelections, templateIds:[...selTmpls], sendSms, sendEmail, campaignId, includeBlasted: incBlasted
      }, authH(token));
      setResults(r.data); toast.success(`✅ Sent: ${r.data.sent}  Failed: ${r.data.failed}`);
      loadHistory();
    } catch(e) { toast.error(e.response?.data?.error || 'Blast failed'); } finally { setSending(false); }
  };

  const selCount = Object.keys(selSubs).length;
  return (
    <div style={{ padding:24, maxWidth:1400 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>📢 Lead Blast</h2>
        {history && <span style={{ fontSize:13, fontWeight:600, color:'#1a56db' }}>Total blasted: {history.totalBlasted} · Total sends: {history.totalSends}</span>}
      </div>
      <p style={{ margin:'0 0 16px', fontSize:13, color:'#6b7280' }}>Select inactive subaccounts, set blast %, pick templates — round-robin SMS or email.</p>
      <div style={{ display:'flex', borderBottom:'2px solid #e5e7eb', marginBottom:20 }}>
        {[{k:'send',label:'📤 Send Blast'},{k:'history',label:'🕓 Blast History'}].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)} style={{ padding:'10px 20px', border:'none', background:'none', cursor:'pointer', fontWeight:tab===t.k?700:500, fontSize:14, color:tab===t.k?'#1a56db':'#6b7280', borderBottom:tab===t.k?'2px solid #1a56db':'2px solid transparent', marginBottom:-2 }}>{t.label}</button>
        ))}
      </div>
      {tab==='send' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:20, alignItems:'start' }}>
          <div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14, alignItems:'center' }}>
              <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 16px', display:'flex', gap:14, flexWrap:'wrap' }}>
                <label style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, cursor:'pointer' }}><input type="checkbox" checked={sendSms} onChange={e=>setSendSms(e.target.checked)}/> 📱 SMS</label>
                <label style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, cursor:'pointer' }}><input type="checkbox" checked={sendEmail} onChange={e=>setSendEmail(e.target.checked)}/> 📧 Email</label>
                <label style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, cursor:'pointer' }}><input type="checkbox" checked={incBlasted} onChange={e=>setIncBlasted(e.target.checked)}/> Include previously blasted</label>
              </div>
              <button onClick={loadSubaccounts} style={{ padding:'8px 14px', borderRadius:7, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontSize:12 }}><FiRefreshCw size={13}/> Refresh</button>
            </div>
            <SubaccountList subaccounts={filteredSubs} subLoading={subLoading} search={search} setSearch={setSearch} selSubs={selSubs} toggleSub={toggleSub} setPct={setPct} expanded={expanded} setExpanded={setExpanded} selCount={selCount} totalLeadsToBlast={totalLeadsToBlast} token={token} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <TemplatePanel templates={templates} selTmpls={selTmpls} toggleT={toggleT} />
            {results && <ResultsBanner results={results} />}
            <button onClick={handleSend} disabled={disabled} style={{ padding:'14px 24px', borderRadius:10, border:'none', background:disabled?'#9ca3af':'#1a56db', color:'#fff', fontWeight:700, fontSize:15, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <FiSend size={16}/> {sending ? 'Sending…' : `Blast ~${totalLeadsToBlast.toLocaleString()} leads`}
            </button>
          </div>
        </div>
      )}
      {tab==='history' && <HistoryPanel history={history} histLoading={histLoading} onRefresh={loadHistory} />}
    </div>
  );
}

function SubaccountList({ subaccounts, subLoading, search, setSearch, selSubs, toggleSub, setPct, expanded, setExpanded, selCount, totalLeadsToBlast, token }) {
  const [cc, setCc] = React.useState({});
  const toggleExpand = (lid) => {
    const willOpen = !expanded[lid];
    setExpanded(p => ({ ...p, [lid]: willOpen }));
    if (willOpen && !cc[lid]) {
      setCc(p => ({ ...p, [lid]: { loading:true, contacts:[] } }));
      axios.get(`/api/admin/ghl/subaccounts/${lid}/contacts?page=1&limit=500`, { headers:{ Authorization:`Bearer ${token}` } })
        .then(r => setCc(p => ({ ...p, [lid]: { loading:false, contacts: r.data.contacts||[] } })))
        .catch(() => setCc(p => ({ ...p, [lid]: { loading:false, contacts:[] } })));
    }
  };
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden' }}>
      <div style={{ padding:'12px 18px', borderBottom:'1px solid #f3f4f6', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
        <span style={{ fontWeight:700, fontSize:15 }}>Inactive Subaccounts ({subaccounts.length})</span>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, ID, agent email…"
          style={{ flex:1, minWidth:200, padding:'6px 12px', borderRadius:7, border:'1px solid #d1d5db', fontSize:13, outline:'none' }}/>
        {selCount>0 && <span style={{ fontSize:12, fontWeight:600, color:'#1a56db' }}>{selCount} selected · ~{totalLeadsToBlast.toLocaleString()} leads</span>}
      </div>
      {subLoading ? <div style={{padding:40,textAlign:'center',color:'#9ca3af'}}>Loading subaccounts…</div>
      : subaccounts.length===0 ? <div style={{padding:40,textAlign:'center',color:'#9ca3af',fontSize:13}}>No inactive subaccounts (master OFF) found.</div>
      : <div style={{ maxHeight:800, overflowY:'auto' }}>
          {subaccounts.map(s => {
            const isSel = selSubs[s.locationId] !== undefined;
            const pct = selSubs[s.locationId] || 10;
            const cnt = s.contactCount || 0;
            const blastCnt = Math.max(1, Math.floor(cnt * pct / 100));
            const isExp = !!expanded[s.locationId];
            const cached = cc[s.locationId];
            const allContacts = cached?.contacts || [];
            const previewContacts = allContacts.slice(0, blastCnt);
            return (
              <div key={s.locationId} style={{ borderBottom:'1px solid #f3f4f6' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 18px', background:isSel?'#eff6ff':'#fff' }}>
                  <div onClick={()=>toggleSub(s.locationId)} style={{ flexShrink:0, cursor:'pointer' }}>
                    <input type="checkbox" checked={isSel} onChange={()=>{}} style={{ width:15, height:15, accentColor:'#1a56db' }}/>
                  </div>
                  <span style={{ color:'#9ca3af', flexShrink:0, cursor:'pointer' }} onClick={()=>toggleExpand(s.locationId)}>
                    {isExp ? <FiChevronDown size={15}/> : <FiChevronRight size={15}/>}
                  </span>
                  <div style={{ flex:1, minWidth:0, cursor:'pointer' }} onClick={()=>toggleExpand(s.locationId)}>
                    <div style={{ fontWeight:700, fontSize:14, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.locationName||s.locationId}</div>
                    <div style={{ fontSize:11, color:'#9ca3af', marginTop:2, display:'flex', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontFamily:'monospace' }}>{s.locationId}</span>
                      {s.assignedAgentEmail && <span style={{ color:'#6b7280' }}>👤 {s.assignedAgentEmail}</span>}
                      {cnt>0 ? <span style={{ background:'#f0fdf4', color:'#15803d', borderRadius:10, padding:'1px 7px', fontWeight:600 }}>{cnt.toLocaleString()} contacts</span>
                             : <span style={{ background:'#f3f4f6', color:'#9ca3af', borderRadius:10, padding:'1px 7px' }}>— contacts</span>}
                    </div>
                  </div>
                  {isSel ? (
                    <div style={{ flexShrink:0, width:220 }} onClick={e=>e.stopPropagation()}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <span style={{ fontSize:13, fontWeight:700, color:'#1a56db' }}>{pct}%</span>
                        <span style={{ fontSize:11, color:'#6b7280' }}>→ ~{blastCnt.toLocaleString()} leads</span>
                      </div>
                      <input type="range" min={1} max={100} value={pct} onChange={e=>setPct(s.locationId, Number(e.target.value))}
                        style={{ width:'100%', accentColor:'#1a56db', cursor:'pointer' }}/>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'#9ca3af' }}><span>1%</span><span>50%</span><span>100%</span></div>
                    </div>
                  ) : <div style={{ flexShrink:0, width:220, textAlign:'right' }}><span style={{ fontSize:11, color:'#d1d5db' }}>Select to set %</span></div>}
                </div>
                {/* Expanded contact preview */}
                {isExp && (
                  <div style={{ background:'#f8faff', borderTop:'1px solid #e5e7eb', padding:'12px 18px 16px 48px' }}>
                    {cached?.loading ? <div style={{fontSize:13,color:'#9ca3af'}}>Loading contacts…</div>
                    : allContacts.length===0 ? <div style={{fontSize:13,color:'#9ca3af'}}>No cached contacts. Sync GHL Contacts first.</div>
                    : <>
                        <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>
                          Showing {previewContacts.length} of {allContacts.length} contacts at {pct}%
                          {!isSel && <span style={{ color:'#9ca3af', fontWeight:400 }}> · check the box to include in blast</span>}
                        </div>
                        <div style={{ overflowX:'auto', maxHeight:300, overflowY:'auto' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                            <thead><tr style={{ background:'#eff6ff', position:'sticky', top:0 }}>
                              {['Name','Email','Phone','Tags'].map(h=><th key={h} style={{ padding:'6px 10px', textAlign:'left', fontWeight:600, color:'#374151', whiteSpace:'nowrap' }}>{h}</th>)}
                            </tr></thead>
                            <tbody>
                              {previewContacts.map((c,i)=>(
                                <tr key={c.id||i} style={{ background:i%2===0?'#fff':'#f9fafb', borderBottom:'1px solid #f0f4ff' }}>
                                  <td style={{padding:'6px 10px',fontWeight:500}}>{[c.firstName,c.lastName].filter(Boolean).join(' ')||'—'}</td>
                                  <td style={{padding:'6px 10px',color:'#1a56db'}}>{c.email||'—'}</td>
                                  <td style={{padding:'6px 10px',color:'#6b7280'}}>{c.phone||'—'}</td>
                                  <td style={{padding:'6px 10px'}}>{(c.tags||[]).length>0?c.tags.slice(0,3).map(t=><span key={t} style={{display:'inline-block',background:'#eff6ff',color:'#1d4ed8',borderRadius:8,padding:'1px 6px',fontSize:10,marginRight:3}}>{t}</span>):<span style={{color:'#d1d5db'}}>—</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {allContacts.length > blastCnt && (
                          <div style={{ marginTop:8, fontSize:11, color:'#9ca3af' }}>
                            +{(allContacts.length - blastCnt).toLocaleString()} more not included — increase % to add more
                          </div>
                        )}
                      </>}
                  </div>
                )}
              </div>
            );
          })}
        </div>}
    </div>
  );
}

function ResultsBanner({ results }) {
  return (
    <div style={{ background:results.failed===0?'#f0fdf4':'#fffbeb', border:`1px solid ${results.failed===0?'#86efac':'#fde68a'}`, borderRadius:10, padding:'14px 18px', marginBottom:20, fontSize:14 }}>
      ✅ Sent: <strong>{results.sent}</strong> &nbsp;|&nbsp; ❌ Failed: <strong>{results.failed}</strong>
      {results.errors?.length>0&&<details style={{marginTop:8}}><summary style={{cursor:'pointer',fontSize:12,color:'#6b7280'}}>Errors ({results.errors.length})</summary><ul style={{margin:'8px 0 0',paddingLeft:18,fontSize:12,color:'#dc2626'}}>{results.errors.map((e,i)=><li key={i}>{e}</li>)}</ul></details>}
    </div>
  );
}



function HistoryPanel({ history, histLoading, onRefresh }) {
  if (histLoading) return <div style={{padding:60,textAlign:'center',color:'#9ca3af'}}>Loading history…</div>;
  if (!history)    return <div style={{padding:60,textAlign:'center',color:'#9ca3af'}}>No data yet.</div>;
  const { totalBlasted=0, totalSends=0, contacts=[], byLocation=[] } = history;
  return (
    <div>
      <div style={{ display:'flex', gap:16, marginBottom:20, flexWrap:'wrap' }}>
        {[{l:'Unique leads blasted',v:totalBlasted,c:'#1a56db'},{l:'Total sends',v:totalSends,c:'#0e9f6e'},{l:'Subaccounts involved',v:byLocation.length,c:'#d97706'}].map(s=>(
          <div key={s.l} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'14px 20px', minWidth:160 }}>
            <div style={{ fontSize:28, fontWeight:800, color:s.c }}>{s.v}</div>
            <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{s.l}</div>
          </div>
        ))}
        <button onClick={onRefresh} style={{ marginLeft:'auto', padding:'10px 16px', borderRadius:8, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:13, alignSelf:'center' }}>
          <FiRefreshCw size={14}/> Refresh
        </button>
      </div>
      {byLocation.length>0 && (
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden', marginBottom:20 }}>
          <div style={{ padding:'12px 18px', borderBottom:'1px solid #f3f4f6', fontWeight:700, fontSize:14 }}>📍 By Subaccount</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:1, background:'#f3f4f6' }}>
            {byLocation.map(loc=>(
              <div key={loc.locationId} style={{ background:'#fff', padding:'12px 16px' }}>
                <div style={{ fontSize:14, fontWeight:600, color:'#111827' }}>{loc.locationName||'Unknown'}</div>
                <div style={{ fontSize:10, color:'#9ca3af', fontFamily:'monospace', marginBottom:4 }}>{loc.locationId}</div>
                {loc.agentEmail&&<div style={{ fontSize:11, color:'#6b7280', marginBottom:6 }}>{loc.agentEmail}</div>}
                <span style={{ fontSize:12, fontWeight:700, color:'#1a56db' }}>{loc.count} lead{loc.count!==1?'s':''} blasted</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden' }}>
        <div style={{ padding:'12px 18px', borderBottom:'1px solid #f3f4f6', fontWeight:700, fontSize:14 }}>📋 All Blasted Contacts ({contacts.length})</div>
        {contacts.length===0?<div style={{padding:40,textAlign:'center',color:'#9ca3af'}}>No contacts blasted yet.</div>
        :<div style={{ maxHeight:600, overflowY:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr style={{ background:'#f9fafb', position:'sticky', top:0 }}>
              {['Name','Email','Phone','Subaccount','Agent','Blasts','Last Sent'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600, color:'#374151', whiteSpace:'nowrap' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {contacts.map((c,i)=>(
                <tr key={c.contactId} style={{ background:i%2===0?'#fff':'#f9fafb', borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{padding:'8px 12px',fontWeight:500}}>{[c.firstName,c.lastName].filter(Boolean).join(' ')||<span style={{color:'#d1d5db'}}>—</span>}</td>
                  <td style={{padding:'8px 12px',color:'#1a56db'}}>{c.email||'—'}</td>
                  <td style={{padding:'8px 12px',color:'#6b7280'}}>{c.normalizedPhone||'—'}</td>
                  <td style={{padding:'8px 12px'}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#111827'}}>{c.locationName||'—'}</div>
                    <div style={{fontSize:10,color:'#9ca3af',fontFamily:'monospace'}}>{c.locationId}</div>
                  </td>
                  <td style={{padding:'8px 12px',fontSize:12,color:'#6b7280'}}>{c.agentEmail||<span style={{color:'#d1d5db'}}>—</span>}</td>
                  <td style={{padding:'8px 12px',textAlign:'center',fontWeight:700,color:'#1a56db'}}>{c.blastCount}</td>
                  <td style={{padding:'8px 12px',color:'#6b7280',fontSize:12}}>{c.blastSentAt?new Date(c.blastSentAt).toLocaleString():'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </div>
    </div>
  );
}

function TemplatePanel({ templates, selTmpls, toggleT }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden' }}>
      <div style={{ padding:'14px 18px', borderBottom:'1px solid #f3f4f6' }}>
        <span style={{ fontWeight:700, fontSize:15 }}>Templates</span>
        <span style={{ marginLeft:10, fontSize:12, color:'#6b7280' }}>{selTmpls.size} selected · round-robin</span>
      </div>
      {templates.length===0 ? <div style={{padding:32,textAlign:'center',color:'#9ca3af',fontSize:13}}>No templates. Create them in the Templates tab.</div>
      : <div style={{ maxHeight:560, overflowY:'auto' }}>
          {templates.map((t,i)=>{ const sel=selTmpls.has(t.id); return (
            <div key={t.id} onClick={()=>toggleT(t.id)} style={{ padding:'12px 16px', borderBottom:'1px solid #f3f4f6', cursor:'pointer', background:sel?'#eff6ff':'#fff', display:'flex', gap:10, alignItems:'flex-start' }}>
              <input type="checkbox" checked={sel} onChange={()=>{}} style={{ width:15,height:15,marginTop:2,flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#111827', marginBottom:4 }}>{t.name||`Template ${i+1}`}</div>
                <div style={{ fontSize:12, color:'#6b7280', lineHeight:1.4, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical' }}>{t.content}</div>
              </div>
            </div>
          );})}
        </div>
      }
    </div>
  );
}
